import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { OfflineSyncProvider } from '@/contexts/OfflineSyncContext';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { useAuthInit } from '@/hooks/useAuthInit';

// Pages — eagerly loaded (small / critical path)
import LegacyDashboard from '@/pages/LegacyDashboard';
import Accounts from '@/pages/Accounts';
import Transactions from '@/pages/Transactions';
import Budgets from '@/pages/Budgets';
import Goals from '@/pages/Goals';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Settings from '@/pages/Settings';
import Subscriptions from '@/pages/Subscriptions';
import Notifications from '@/pages/Notifications';
import Bills from '@/pages/Bills';
import Receipts from '@/pages/Receipts';
import Landing from '@/pages/Landing';
import SharedReceipt from '@/pages/SharedReceipt';
import Review from '@/pages/Review';

// Pages — lazy loaded [T74P0-CODESPLIT] (heavy / rarely visited)
const Rules = lazy(() => import('@/pages/Rules'));
const Extensions = lazy(() => import('@/pages/Extensions'));
const Insights = lazy(() => import('@/pages/Insights'));
const Dashboards = lazy(() => import('@/pages/Dashboards'));
// [T246.5-FE-PAGE] The /widgets/insights page that curates which widget
// graphs are enabled on the transactions page. Lazy-loaded to match the
// other heavy pages in this file.
const InsightsGraphManager = lazy(() => import('@/pages/InsightsGraphManager'));
const WidgetLibrary = lazy(() => import('@/pages/WidgetLibrary'));
const Analytics = lazy(() => import('@/pages/Analytics'));
const DashboardBuilder = lazy(() => import('@/pages/DashboardBuilder'));
const DashboardView = lazy(() => import('@/pages/DashboardView'));

/** Route gate for /dashboards/:id — edit mode vs view mode */
function DashboardRouteGate() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  return mode === 'edit' ? (
    <Suspense fallback={<LazyFallback />}><DashboardBuilder /></Suspense>
  ) : (
    <Suspense fallback={<LazyFallback />}><DashboardView /></Suspense>
  );
}

/** Loading fallback for lazy-loaded pages */
function LazyFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="text-center">
        <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-current border-r-transparent" />
        <p className="mt-3 text-sm text-muted-foreground">Loading…</p>
      </div>
    </div>
  );
}

/**
 * Main Application Component
 * Handles routing for FinTrack personal finance application
 */
function App() {
  // Initialize authentication state on app load
  const { isInitialized } = useAuthInit();

  // Show loading spinner while checking authentication
  if (!isInitialized) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
          <p className="mt-4 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <OfflineSyncProvider>
    <div className="min-h-svh bg-background text-foreground">
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/shared/:token" element={<SharedReceipt />} />

        {/* Protected Routes with Layout */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <AppLayout><LegacyDashboard /></AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts"
          element={
            <ProtectedRoute>
              <AppLayout><Accounts /></AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/transactions"
          element={
            <ProtectedRoute>
              <AppLayout><Transactions /></AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/budgets"
          element={
            <ProtectedRoute>
              <AppLayout><Budgets /></AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/goals"
          element={
            <ProtectedRoute>
              <AppLayout><Goals /></AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={<Navigate to="/insights" replace />}
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <AppLayout><Suspense fallback={<LazyFallback />}><Analytics /></Suspense></AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/insights"
          element={
            <ProtectedRoute>
              <AppLayout><Suspense fallback={<LazyFallback />}><Insights /></Suspense></AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards"
          element={
            <ProtectedRoute>
              <AppLayout><Suspense fallback={<LazyFallback />}><Dashboards /></Suspense></AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/:id"
          element={
            <ProtectedRoute>
              <AppLayout><DashboardRouteGate /></AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/widgets"
          element={
            <ProtectedRoute>
              <AppLayout><Suspense fallback={<LazyFallback />}><WidgetLibrary /></Suspense></AppLayout>
            </ProtectedRoute>
          }
        />
        {/* [T246.5-FE-PAGE] Insights Graph Manager — curation UI for
            the 9 widget graphs that appear on the transactions page. */}
        <Route
          path="/widgets/insights"
          element={
            <ProtectedRoute>
              <AppLayout><Suspense fallback={<LazyFallback />}><InsightsGraphManager /></Suspense></AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <AppLayout><Settings /></AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscriptions"
          element={
            <ProtectedRoute>
              <AppLayout><Subscriptions /></AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/rules"
          element={
            <ProtectedRoute>
              <AppLayout><Suspense fallback={<LazyFallback />}><Rules /></Suspense></AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <AppLayout><Notifications /></AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/extensions"
          element={
            <ProtectedRoute>
              <AppLayout><Suspense fallback={<LazyFallback />}><Extensions /></Suspense></AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bills"
          element={
            <ProtectedRoute>
              <AppLayout><Bills /></AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/receipts"
          element={
            <ProtectedRoute>
              <AppLayout><Receipts /></AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/review"
          element={
            <ProtectedRoute>
              <AppLayout><Review /></AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Redirect unknown routes to welcome */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {/* Global toast notifications — available on ALL routes including /login and /register */}
      <Toaster />
    </div>
    </OfflineSyncProvider>
  );
}

export default App;
